/**
 * useCrypto PreKey Management
 *
 * Pre-key replenishment logic: checks local pre-key count and generates more
 * when below the threshold, uploading new public keys to the server.
 *
 * Extracted from useCrypto.ts for maintainability.
 * Accepts `get`/`set` from the Zustand store to avoid circular imports.
 */

import { generatePreKeys, toBase64 } from "@kollegeplus/crypto";
import { api } from "../lib/api";
import { saveDeviceKeys, getFirstDeviceKeys } from "../lib/crypto-storage";
import { e2eeLog } from "../lib/e2ee-logger";
import { withBusy } from "../lib/busy-guard";
import { reportE2eeError } from "../lib/e2ee-telemetry";
import type { CryptoState } from "./useCrypto";

/** Minimum pre-key threshold — replenish when below this */
const PRE_KEY_REPLENISH_THRESHOLD = 50;
/** How many pre-keys to generate when replenishing */
const PRE_KEY_BATCH_SIZE = 100;
/** F25: hard cap on locally stored pre-key privates (~200 B each). Never
 *  trimmed by server-count drift — only by this cap (oldest first). */
const MAX_LOCAL_PRE_KEYS = 1000;

// ─── In-flight guard ─────────────────────────────────────────────────────────
// Prevents double replenish when _connected and visibilitychange both fire in
// the same tick (e.g. tab resumes from background with a dead WS that just
// reconnected). Mirrors the heartbeatInFlight pattern in useCrypto.ts.
let replenishInFlight = false;

// ─── checkAndReplenishPreKeys ─────────────────────────────────────────────────

/**
 * Check the local pre-key count and upload a fresh batch if below the threshold.
 * Also accepts an optional serverPreKeyCount to detect client/server drift:
 * if the server has fewer pre-keys than the client, we sync immediately.
 *
 * Guarded by busy-guard — called fire-and-forget after encrypt/decrypt paths,
 * so a reload must not chop it mid-way (would leave local/server pre-key state
 * out of sync).
 */
export function checkAndReplenishPreKeys(
  ...args: Parameters<typeof _checkAndReplenishPreKeysImpl>
): Promise<void> {
  return withBusy(() => _checkAndReplenishPreKeysImpl(...args));
}

async function _checkAndReplenishPreKeysImpl(
  get: () => CryptoState,
  set: (partial: Partial<CryptoState>) => void,
  serverPreKeyCount?: number,
): Promise<void> {
  if (replenishInFlight) return;
  replenishInFlight = true;
  try {
    const state = get();
    if (!state.deviceKeys || !state.deviceId) return;

    // Check how many pre-keys we have locally
    const stored = await getFirstDeviceKeys();
    if (!stored) return;

    const localPreKeyCount = stored.preKeys.length;

    // F25: NEVER delete local pre-key privates when server < local. The server
    // pops a pub OPK at BUNDLE-FETCH time, but the matching X3DH init only
    // reaches this device when the sender's message (or a queued session
    // reset — pending for hours to DAYS) actually arrives. The old count-based
    // trim ("remove oldest — they were consumed") deleted exactly those
    // in-flight privates, so the init later failed with opk_missing /
    // "aes/gcm: invalid ghash tag" — a signature that looks like an IDB
    // rollback but isn't (incidents 2026-07-03 08:13 + 21:50, device
    // web-zW_q0bFpUQJ4ry0a). A private is only safe to drop after its init was
    // processed (the X3DH receive path consumes it) or via the hard cap below.
    if (typeof serverPreKeyCount === "number" && serverPreKeyCount < localPreKeyCount) {
      e2eeLog.log(`Server has ${serverPreKeyCount} pre-keys, local has ${localPreKeyCount} — keeping local privates (may be in-flight)`);
    }

    // F25: hard cap so the local store cannot grow unbounded (~200 B/key).
    // Prune oldest beyond the cap — an init referencing a key that survived
    // 900 newer batches is long dead.
    if (stored.preKeys.length > MAX_LOCAL_PRE_KEYS) {
      const dropCount = stored.preKeys.length - MAX_LOCAL_PRE_KEYS;
      stored.preKeys = stored.preKeys.slice(dropCount);
      await saveDeviceKeys(stored);
      set({
        deviceKeys: {
          ...state.deviceKeys,
          preKeys: state.deviceKeys.preKeys.slice(
            Math.max(0, state.deviceKeys.preKeys.length - MAX_LOCAL_PRE_KEYS),
          ),
        },
      });
    }

    // F25: replenish decisions follow the SERVER pool (that's what peers draw
    // from). Local length is no longer a proxy for it — without the trim it
    // only ever grows. When the caller has no server count (fire-and-forget
    // call sites), fall back to the local count as a fresh-device backstop;
    // the regular heartbeat paths pass the real count.
    const effectiveCount = typeof serverPreKeyCount === "number"
      ? serverPreKeyCount
      : stored.preKeys.length;
    if (effectiveCount >= PRE_KEY_REPLENISH_THRESHOLD) return;

    e2eeLog.log(` Pre-keys low (${localPreKeyCount}), generating ${PRE_KEY_BATCH_SIZE} more...`);

    // Find the highest existing keyId
    const maxKeyId = stored.preKeys.reduce((max, pk) => Math.max(max, pk.keyId), 0);

    // Generate new pre-keys (startId, count)
    const newPreKeys = generatePreKeys(maxKeyId + 1, PRE_KEY_BATCH_SIZE);

    // Serialize for storage
    const serializedNew = newPreKeys.map((pk) => ({
      keyId: pk.keyId,
      privateKey: toBase64(pk.keyPair.privateKey),
      publicKey: toBase64(pk.keyPair.publicKey),
    }));

    // F19a: Persist-first with read-back verify. Inverts the F14a-e upload-first
    // ordering after a production incident where Meikel's device hit the OTHER
    // failure mode: server had pub OPKs that the client had no matching priv for
    // (IDB save silently dropped after upload). Result: every X3DH receive on
    // that device produced `aes/gcm: invalid ghash tag` — surfaced by F18b/F18c
    // diagnostic logging. New invariant: a pub OPK is only published if its
    // priv half is durably in IDB AND verified present via read-back. The
    // inverse failure (upload fails after IDB save) is benign — the existing
    // serverPreKeyCount drift-sync above already trims local down to match server.
    const publicPreKeys = newPreKeys.map((pk) => ({
      keyId: pk.keyId,
      publicKey: toBase64(pk.keyPair.publicKey),
    }));

    // 1. Persist to IDB FIRST.
    const nextStored = { ...stored, preKeys: [...stored.preKeys, ...serializedNew] };
    await saveDeviceKeys(nextStored);

    // 2. Read-back verify: every new keyId must round-trip out of IDB before we
    //    expose its public half to peers. If IDB swallowed the write (quota,
    //    corruption, racy clear), bail without uploading.
    const verify = await getFirstDeviceKeys();
    // Distinguish "IDB returned no record at all" from "record exists but is
    // missing specific keyIds" — otherwise telemetry reports the verify-null
    // case as "all keyIds missing", which is misleading (the real failure is
    // that getFirstDeviceKeys() returned null/undefined right after a
    // successful save).
    if (!verify) {
      e2eeLog.warn(`F19a: pre-key persist read-back verify FAILED — getFirstDeviceKeys() returned null after save. Aborting upload.`);
      reportE2eeError({
        phase: "opk_persist_verify_failed",
        deviceId: state.deviceId,
        error: "verify_returned_null",
      });
      // Same exit path as missing-keys branch: don't mutate in-memory state,
      // don't upload — IDB is in an unknown state.
      return;
    }
    const verifiedIds = new Set(verify.preKeys.map((pk) => pk.keyId));
    const missing = serializedNew.filter((pk) => !verifiedIds.has(pk.keyId)).map((pk) => pk.keyId);
    if (missing.length > 0) {
      e2eeLog.warn(`F19a: pre-key persist read-back verify FAILED — ${missing.length}/${serializedNew.length} keyIds missing from IDB. Aborting upload.`, missing);
      reportE2eeError({
        phase: "opk_persist_verify_failed",
        deviceId: state.deviceId,
        error: `read-back missing ${missing.length}/${serializedNew.length} keyIds: ${missing.slice(0, 10).join(",")}${missing.length > 10 ? "..." : ""}`,
      });
      // Do NOT mutate in-memory state — IDB is in an unknown state, and we
      // never told the server about these keys, so peers can't reference them.
      return;
    }

    // 3. IDB confirmed → safe to publish. In-memory state mirrors IDB now
    //    regardless of upload outcome (IDB is the source of truth).
    const updatedDeviceKeys = {
      ...state.deviceKeys,
      preKeys: [
        ...state.deviceKeys.preKeys,
        ...newPreKeys,
      ],
    };
    set({ deviceKeys: updatedDeviceKeys });

    try {
      await api.post(`/users/me/devices/${state.deviceId}/prekeys`, {
        preKeys: publicPreKeys,
      });
      e2eeLog.log(` Persisted and uploaded ${PRE_KEY_BATCH_SIZE} new pre-keys`);
    } catch (err) {
      // Upload failed after successful IDB persist. Don't roll back: leftover
      // local priv OPKs are harmless (they're just never referenced by peers).
      // The serverPreKeyCount drift-sync at the top of the next replenish
      // round will reconcile — it trims local down when server < local, so the
      // unused entries get pruned naturally.
      e2eeLog.warn("F19a: Failed to upload pre-keys after IDB persist — local has more keys than server, drift-sync will reconcile next round:", err);
    }
  } finally {
    replenishInFlight = false;
  }
}
